import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  adoptManageHobbyShops,
  createManageHobbyShop,
  deleteManageHobbyShop,
  listManageHobbyShops,
  reorderManageHobbyShops,
  updateManageHobbyShop,
  type HobbyShopInput,
  type LocationRow,
} from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ManageClubNav, moveId, useManagedClub } from "../components/ManageClubNav";
import { PencilFillIcon, Trash3FillIcon } from "../components/icons";
import Toast from "../components/Toast";
import { MapPreview } from "../components/MapPreview";

const EMPTY: HobbyShopInput = { name: "", address: "", phone: "", hours: "", website: "", published: true };

/** /manage/:clubId/hobby-shops -- shops the club promotes on its Hobby
 *  Shops page: name, address (with a map preview once saved), phone, hours. */
export default function ManageHobbyShopsPage() {
  const { clubId = "" } = useParams();
  const { user } = useAuth();
  const { club, error: clubError } = useManagedClub(clubId);
  const [shops, setShops] = useState<LocationRow[] | null>(null);
  const [form, setForm] = useState<HobbyShopInput>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function refresh() {
    listManageHobbyShops(clubId)
      .then((res) => setShops(res.shops))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load hobby shops."));
  }
  useEffect(refresh, [clubId]);

  async function run(action: () => Promise<string | void>) {
    setError(null);
    setBusy(true);
    try {
      const msg = await action();
      if (msg) setInfo(msg);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  function startEdit(s: LocationRow) {
    setEditingId(s.locationId);
    setForm({
      name: s.name,
      address: s.address ?? "",
      phone: s.phone ?? "",
      hours: s.hours ?? "",
      website: s.website ?? "",
      published: s.published !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function resetForm() {
    setEditingId(null);
    setForm(EMPTY);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    run(async () => {
      if (editingId) {
        await updateManageHobbyShop(clubId, editingId, form);
      } else {
        // Stay on the new shop after adding it, so its map preview shows.
        const { shop } = await createManageHobbyShop(clubId, form);
        setEditingId(shop.locationId);
      }
      refresh();
      return `Saved ${form.name}.`;
    });
  }

  function move(id: string, delta: -1 | 1) {
    if (!shops) return;
    const ids = moveId(shops.map((s) => s.locationId), id, delta);
    run(async () => setShops((await reorderManageHobbyShops(clubId, ids)).shops));
  }

  if (clubError) return <p className="err">{clubError}</p>;
  if (!club) return <p>Loading…</p>;

  // The map previews the saved address, not what's half-typed in the box.
  const saved = shops?.find((s) => s.locationId === editingId);

  return (
    <div>
      <ManageClubNav club={club} />
      <h1>{club.name} hobby shops</h1>
      {error && <p className="err">{error}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}

      <form onSubmit={handleSubmit} className="card">
        <h3>{editingId ? "Edit hobby shop" : "Add a hobby shop"}</h3>
        <label htmlFor="shop-name">Name</label>
        <input id="shop-name" value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <div style={{ marginTop: "0.75rem" }}>
          <label htmlFor="shop-address">Address</label>
          <input
            id="shop-address"
            autoComplete="street-address"
            placeholder="123 Main St, Spokane, WA 99201"
            value={form.address ?? ""}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
        </div>
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <div>
            <label htmlFor="shop-phone">Phone</label>
            <input id="shop-phone" type="tel" value={form.phone ?? ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label htmlFor="shop-website">Website</label>
            <input id="shop-website" placeholder="https://" value={form.website ?? ""} onChange={(e) => setForm({ ...form, website: e.target.value })} />
          </div>
        </div>
        <div style={{ marginTop: "0.75rem" }}>
          <label htmlFor="shop-hours">Business hours (one line per day or range)</label>
          <textarea
            id="shop-hours"
            rows={4}
            placeholder={"Mon–Fri 10am–6pm\nSat 10am–4pm\nSun closed"}
            value={form.hours ?? ""}
            onChange={(e) => setForm({ ...form, hours: e.target.value })}
          />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.75rem" }}>
          <input type="checkbox" style={{ width: "auto" }} checked={form.published !== false} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
          Show on the Hobby Shops page
        </label>
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
          <button type="submit" disabled={busy}>{editingId ? "Save changes" : "Add shop"}</button>
          {editingId && <button type="button" className="secondary" onClick={resetForm}>Done</button>}
        </div>
        {saved?.address && <MapPreview address={saved.address} name={saved.name} />}
      </form>

      <h2>Page order</h2>
      {shops && shops.length === 0 && <p className="muted">No hobby shops yet.</p>}
      {shops && shops.length > 0 && (
        <div className="table-scroll"><table>
          <thead>
            <tr><th>Order</th><th>Name</th><th>Phone</th><th>Shown</th><th /></tr>
          </thead>
          <tbody>
            {shops.map((s, i) => (
              <tr key={s.locationId}>
                <td>
                  <span className="order-buttons">
                    <button type="button" className="secondary" disabled={busy || i === 0} onClick={() => move(s.locationId, -1)} aria-label={`Move ${s.name} up`}>↑</button>
                    <button type="button" className="secondary" disabled={busy || i === shops.length - 1} onClick={() => move(s.locationId, 1)} aria-label={`Move ${s.name} down`}>↓</button>
                  </span>
                </td>
                <td>
                  {s.name}
                  {s.address && <div className="muted" style={{ fontSize: 13 }}>{s.address}</div>}
                </td>
                <td>{s.phone ?? ""}</td>
                <td>{s.published === false ? "Hidden" : "Yes"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button type="button" className="link" title="Edit" aria-label={`Edit ${s.name}`} onClick={() => startEdit(s)}>
                    <PencilFillIcon />
                  </button>
                  <button
                    type="button"
                    className="link"
                    title="Delete"
                    aria-label={`Delete ${s.name}`}
                    onClick={() =>
                      confirm(`Delete ${s.name}?`) &&
                      run(async () => {
                        await deleteManageHobbyShop(clubId, s.locationId);
                        if (editingId === s.locationId) resetForm();
                        refresh();
                        return `Deleted ${s.name}.`;
                      })
                    }
                  >
                    <Trash3FillIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {user?.isAdmin && (
        <p className="muted" style={{ marginTop: "1.5rem" }}>
          Site admin:{" "}
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={() =>
              confirm(`Move every site-wide hobby shop (from Admin) into ${club.name}?`) &&
              run(async () => {
                const res = await adoptManageHobbyShops(clubId);
                setShops(res.shops);
                return `Moved ${res.adopted} shop${res.adopted === 1 ? "" : "s"} into ${club.name}.`;
              })
            }
          >
            Adopt site-wide hobby shops
          </button>
        </p>
      )}
    </div>
  );
}
